import React, { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { produksjonAPI, talentAPI, ProduksjonsKategoriTalentMal, Talent, TalentKategori } from '../../services/api';
import SelectableTalentTree from './SelectableTalentTree';
import { TalentSelection } from './SelectableTalentList';

interface Props {
  kategoriId: number;
  kategoriNavn: string;
  onSave: () => void;
}

const TalentMalEditor: React.FC<Props> = ({ kategoriId, kategoriNavn, onSave }) => {
  const [kategorier, setKategorier] = useState<TalentKategori[]>([]);
  const [selectionsByKategori, setSelectionsByKategori] = useState<Map<number, TalentSelection[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apneKategorier, setApneKategorier] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoriId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // VIKTIG: Reset state først for å unngå at gammel data vises
      setSelectionsByKategori(new Map());
      setApneKategorier(new Set());
      
      const [kategoriData, talentData, talentMalData] = await Promise.all([
        talentAPI.getAllKategorier(),
        talentAPI.getAll(),
        produksjonAPI.getTalentMal(kategoriId),
      ]);

      console.log(`[TalentMalEditor] KategoriId: ${kategoriId}, Talent-mal count:`, talentMalData.talentMal.length);

      setKategorier(kategoriData.kategorier);

      // Grupper talenter etter talent-kategori ID
      const selMap = new Map<number, TalentSelection[]>();
      
      talentData.talenter.forEach((talent: Talent) => {
        if (!selMap.has(talent.kategori_id)) {
          selMap.set(talent.kategori_id, []);
        }

        // Finn om dette talentet allerede er i malen
        const existingMal = talentMalData.talentMal.find((m: ProduksjonsKategoriTalentMal) => m.talent_id === talent.id);

        selMap.get(talent.kategori_id)!.push({
          talent,
          selected: !!existingMal,
          antall: existingMal?.antall || 1,
          beskrivelse: existingMal?.beskrivelse || '',
        });
      });

      // Sorter talenter innenfor hver kategori
      selMap.forEach((selections) => {
        selections.sort((a, b) => a.talent.navn.localeCompare(b.talent.navn));
      });

      console.log('[TalentMalEditor] Nye selections per kategori:', Array.from(selMap.entries()).map(([katId, sels]) => ({
        kategoriId: katId,
        totalTalents: sels.length,
        selectedCount: sels.filter(s => s.selected).length,
        talents: sels.map(s => ({ navn: s.talent.navn, selected: s.selected }))
      })));

      setSelectionsByKategori(selMap);

      // Start alltid kollapset ved valg/refresh av kategori
      setApneKategorier(new Set<number>());

    } catch (err: any) {
      setError('Kunne ikke laste talenter');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleKategori = (kategoriId: number) => {
    setApneKategorier(prev => {
      const next = new Set(prev);
      if (next.has(kategoriId)) {
        next.delete(kategoriId);
      } else {
        next.add(kategoriId);
      }
      return next;
    });
  };

  const handleTalentToggle = (kategoriId: number, index: number) => {
    setSelectionsByKategori(prev => {
      const next = new Map(prev);
      const selections = [...(next.get(kategoriId) || [])];
      const selection = { ...selections[index] };
      selection.selected = !selection.selected;
      if (selection.selected && selection.antall === 0) {
        selection.antall = 1;
      }
      selections[index] = selection;
      next.set(kategoriId, selections);
      return next;
    });
  };

  const handleAntallChange = (kategoriId: number, index: number, antall: number) => {
    setSelectionsByKategori(prev => {
      const next = new Map(prev);
      const selections = [...(next.get(kategoriId) || [])];
      const selection = { ...selections[index] };
      selection.antall = Math.max(1, antall);
      selection.selected = true;
      selections[index] = selection;
      next.set(kategoriId, selections);
      return next;
    });
  };

  const handleBeskrivelseChange = (kategoriId: number, index: number, beskrivelse: string) => {
    setSelectionsByKategori(prev => {
      const next = new Map(prev);
      const selections = [...(next.get(kategoriId) || [])];
      const selection = { ...selections[index] };
      selection.beskrivelse = beskrivelse;
      selections[index] = selection;
      next.set(kategoriId, selections);
      return next;
    });
  };

  const handleClearKategori = (kategoriId: number) => {
    setSelectionsByKategori(prev => {
      const next = new Map(prev);
      const selections = (next.get(kategoriId) || []).map(s => ({ ...s, selected: false }));
      next.set(kategoriId, selections);
      return next;
    });
  };

  const getChildren = (parentId: number): TalentKategori[] => {
    return kategorier.filter(k => k.parent_id === parentId);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Hent eksisterende mal
      const talentMalData = await produksjonAPI.getTalentMal(kategoriId);
      const existingMal = talentMalData.talentMal;

      // Samle alle selections
      const allSelections: TalentSelection[] = [];
      selectionsByKategori.forEach((selections) => {
        allSelections.push(...selections);
      });

      const selectedTalenter = allSelections.filter(s => s.selected);

      // Finn talenter som skal fjernes
      for (const mal of existingMal) {
        const stillSelected = selectedTalenter.find(s => s.talent.id === mal.talent_id);
        if (!stillSelected) {
          await produksjonAPI.removeTalentFromMal(kategoriId, mal.id);
        }
      }

      // Legg til eller oppdater valgte talenter
      for (const selection of selectedTalenter) {
        const existingEntry = existingMal.find(m => m.talent_id === selection.talent.id);
        
        if (existingEntry) {
          // Oppdater eksisterende
          await produksjonAPI.updateTalentInMal(kategoriId, existingEntry.id, {
            antall: selection.antall,
            beskrivelse: selection.beskrivelse || undefined,
          });
        } else {
          // Legg til ny
          await produksjonAPI.addTalentToMal(kategoriId, {
            talentId: selection.talent.id,
            antall: selection.antall,
            beskrivelse: selection.beskrivelse || undefined,
          });
        }
      }

      onSave();
      await fetchData(); // Refresh for å få oppdaterte data

    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  const getTotalSelected = () => {
    let total = 0;
    selectionsByKategori.forEach((selections) => {
      total += selections.filter(s => s.selected).length;
    });
    return total;
  };

  const getTotalAntall = () => {
    let total = 0;
    selectionsByKategori.forEach((selections) => {
      total += selections.filter(s => s.selected).reduce((sum, s) => sum + s.antall, 0);
    });
    return total;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Header med statistikk */}
      <div className="flex justify-between items-center mb-6 p-4 bg-primary rounded-lg">
        <div>
          <h3 className="text-lg font-semibold text-primary-foreground">{kategoriNavn}</h3>
          <p className="text-sm text-primary-foreground/90">
            {getTotalSelected()} talenter valgt • {getTotalAntall()} personer totalt
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-white text-primary hover:bg-gray-100"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Lagrer...' : 'Lagre mal'}
        </Button>
      </div>

      {/* Talent-tree med valgmuligheter */}
      <SelectableTalentTree
        kategorier={kategorier}
        selectionsByKategori={selectionsByKategori}
        apneKategorier={apneKategorier}
        onToggle={handleToggleKategori}
        onTalentToggle={handleTalentToggle}
        onAntallChange={handleAntallChange}
        onBeskrivelseChange={handleBeskrivelseChange}
        onClearKategori={handleClearKategori}
        getChildren={getChildren}
      />

      {/* Footer med lagre-knapp */}
      <div className="sticky bottom-0 bg-background p-4 mt-6 border-t flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {getTotalSelected()} talenter • {getTotalAntall()} personer totalt
        </p>
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Lagrer...' : 'Lagre mal'}
        </Button>
      </div>
    </div>
  );
};

export default TalentMalEditor;

